import React, { Component } from "react";
import { Settings, RefreshCw, ExternalLink } from "lucide-react";
import { StatusBadge } from "../../components/ui";
import _ from "lodash";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { NetworkType, TennCards, PaymentMethods } from "../../models/FinancialData";
import { SplitButton } from "primereact/splitbutton";
import { toastService } from "../../services/toastService";


import Loader from "../../components/Loader";
import { PaymentMethodCrudComponent } from "../Banking/PaymentMethodCrudComponent";
import { userPaymentMethodApiService } from "../../services/api/users/UserPaymentMethodApiService";
import { AddUserPaymentMethod } from "./AddUserPaymentMethod";
import { CreateCardPaymentMethodRequest, CreateIbanPaymentMethodRequest, CreateNonIbanPaymentMethodRequest } from "../../models/MerchantModel";
import ClientCardsComponent from "../Issuing/ClientCards";
import history from "../../history";
import { Button } from "primereact/button";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import { Toast } from "primereact/toast";

interface FinancialDataProps {
    userId: number;
    cbsOnboarded?: boolean;
}
interface FinancialDataState {
    isLoading: boolean;
    tennCards: TennCards[];
    paymentMethods: PaymentMethods[];
    isAddPaymentMethodVisible: boolean;
    selectedPaymentMethodType: "Bank" | "NonIBAN";
    hasIssueing: boolean
}

export default class FinancialDataComponent extends Component<FinancialDataProps, FinancialDataState> {
    toast: Toast | null = null;

    constructor(props: any) {
        super(props);

        this.state = {
            isLoading: false,
            tennCards: [],
            paymentMethods: [],
            isAddPaymentMethodVisible: false,
            selectedPaymentMethodType: "Bank",
            hasIssueing: false
        };
    }

    async componentDidMount() {
        if (this.props.cbsOnboarded === true) {
            await this.loadPaymentMethod();
            const hasIssueing = await issuingApiService.getClientDetails(this.props.userId!);
            if (hasIssueing.success && hasIssueing.data) {
                this.setState({ hasIssueing: true });
            }
        }
    }
     onIssueingClientDetailsActionButtonClick = () => {
            history.push({
                pathname: `/issuing/individualId/${this.props.userId!}`
            });
        };


    handleEnrollClient = async () => {

        this.setState({ isLoading: true });

        const response = await issuingApiService.updateClientDetails({
            individualId: this.props.userId!
        });

        if (!response.success) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: response.message,
                life: 3000
            });
        } else {
            this.toast?.show({
                severity: "success",
                summary: "Success",
                detail: "Client enrolled successfully",
                life: 3000
            });
        }

        this.setState({ isLoading: false });
    };
    loadPaymentMethod = async () => {
        this.setState({ isLoading: true });

        const result = await userPaymentMethodApiService.getAll(this.props.userId);
        if (result.success === false) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }

        this.setState({ paymentMethods: result.data, isLoading: false });
    };

    renderCardNetwork = (network: string | NetworkType): React.ReactNode => {
        const isVisa = network === NetworkType.Visa || network === "Visa";
        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide ${isVisa ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"}`}>
                {isVisa ? "VISA" : "Mastercard"}
            </span>
        );
    };

    onDeactivate = () => {
        toastService.success("user Deactivated.", "Action Completed");
    };

    onCvvSend = () => {
        toastService.success("cvv Sent Successfully.", "Action Completed");
    };

    onSendPin = () => {
        toastService.success("PIN Sent Successfully.", "Action Completed");
    };

    actionBodyTemplate = () => {
        const items = [
            {
                label: "Deactivate",
                icon: <RefreshCw size={16} />,
                command: (e: any) => this.onDeactivate(),
            },
            {
                label: "Send CVV",
                icon: <RefreshCw size={16} />,
                command: (e: any) => this.onCvvSend(),
            },
            {
                label: "Send Pin",
                icon: <ExternalLink size={16} />,
                command: (e: any) => this.onSendPin(),
            },
        ];

        return <SplitButton label="" icon={<Settings size={16} />} model={items}></SplitButton>;
    };

    addPaymentMethod = (type: "Bank" | "NonIBAN") => {
        this.setState({ isAddPaymentMethodVisible: true, selectedPaymentMethodType: type });
    };

    onHide = (): void => {
        this.setState({ isAddPaymentMethodVisible: false });
        this.loadPaymentMethod();
    };

    deletePaymentMethod = async (rowData: PaymentMethods) => {
        const deleteResult = await userPaymentMethodApiService.delete(this.props.userId, rowData.value);

        if (deleteResult.success) {
            this.setState({ paymentMethods: this.state.paymentMethods.filter((a) => a.value !== rowData.value) });
        } else {
            toastService.error(deleteResult.message);
        }
    };

    updatePaymentMethod = async (rowData: CreateCardPaymentMethodRequest | CreateIbanPaymentMethodRequest | CreateNonIbanPaymentMethodRequest) => {
        const updateResult = await userPaymentMethodApiService.update(this.props.userId, rowData);
        if (updateResult.success) {
            toastService.success(updateResult.message ?? "Payment method updated.", "Action Completed");
        } else {
            toastService.error(updateResult.message ?? "Operation not allowed.");
        }
        this.loadPaymentMethod();
    };

    render() {
        return (
            <>
                {this.state.isAddPaymentMethodVisible && <AddUserPaymentMethod userId={this.props.userId} onHide={this.onHide} paymentMethodType={this.state.selectedPaymentMethodType} />}
                <div className="mb-6">
                    <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={99999}></Toast>
                    <span className="p-input-icon-left align-right m-3">
                        <i className="pi" />
                        {(!this.state.hasIssueing) && (
                            <Button label="Enrol Issuing" className="p-button-outlined" icon="pi pi-plus" onClick={this.handleEnrollClient} />
                        )}
                        {(this.state.hasIssueing) && (
                            <Button label="View Issuing" className="p-button-outlined" icon="pi pi-eye" onClick={this.onIssueingClientDetailsActionButtonClick} />
                        )}
                    </span>
                    <ClientCardsComponent IndividualId={this.props.userId} />
                </div>
                <div className="mb-6 border-t border-neutral-200 dark:border-neutral-800 pt-4">
                    <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 px-5 pb-2">10n Cards</h4>
                    <DataTable value={this.state.tennCards} emptyMessage="No cards issued">
                        <Column field="id" header="Card Issuing Id"></Column>
                        <Column
                            field="network"
                            header="Network Type"
                            body={(rowData: TennCards) => this.renderCardNetwork(rowData.network)}
                        ></Column>
                        <Column field="type" header="Card Type"></Column>
                        <Column field="provider" header="provider"></Column>
                        <Column
                            field="issueDate"
                            header="Card Issuing Date"
                            body={(rowData: TennCards) => <p>{rowData.issueDate.toDateString()}</p>}
                        ></Column>
                        <Column field="pan" header="Pan"></Column>
                        <Column
                            field="expiryDate"
                            header="Card Expiry Date"
                            body={(rowData: TennCards) => <p>{rowData.expiryDate.toDateString()}</p>}
                        ></Column>
                        <Column field="holderName" header="Card Holder Name"></Column>
                        <Column field="walletId" header="Attached to WalletId"></Column>
                        <Column field="tariff" header="Tariff"></Column>
                        <Column
                            field="active"
                            header="Is Active"
                            body={(rowData: TennCards) => StatusBadge(rowData.active, "Yes", "No")}
                        ></Column>
                        <Column header="Actions" body={this.actionBodyTemplate} />
                    </DataTable>
                </div>
                <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
                    <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 px-5 pb-2">Payment Methods</h4>
                    <PaymentMethodCrudComponent
                        updatePaymentMethod={this.updatePaymentMethod}
                        addPaymentMethod={this.addPaymentMethod}
                        deletePaymentMethod={this.deletePaymentMethod}
                        paymentMethods={this.state.paymentMethods}
                    />
                </div>
            </>
        );
    }
}
